import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallButton from "@/components/CallButton";
import SEO from "@/components/SEO";
import ServiceStructuredData from "@/components/ServiceStructuredData";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOSubHero from "@/components/SEOSubHero";
import { Link } from "react-router-dom";
import { Phone, Wrench, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import heroImage from "@/assets/heroes/reparation-serrure-hero.jpg";
import gallery1 from "@/assets/gallery/reparation-serrure-1.jpg";
import gallery2 from "@/assets/gallery/reparation-serrure-2.jpg";
import gallery3 from "@/assets/gallery/reparation-serrure-3.jpg";

const ReparationSerrure = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Réparation de Serrure Villeurbanne | Dépannage Express 24h/7j"
        description="Réparation de serrure à Villeurbanne : cylindre grippé, serrure endommagée, clé cassée. Intervention rapide par un serrurier professionnel. Devis gratuit."
        keywords="réparation serrure Villeurbanne, dépannage serrure Lyon, cylindre grippé, serrure endommagée 69"
        canonical="https://www.tolstoi-serrurier.fr/reparation-serrure"
        ogUrl="https://www.tolstoi-serrurier.fr/reparation-serrure"
        ogImage="https://www.tolstoi-serrurier.fr/logo.jpg"
        ogType="business.business"
      />
      <ServiceStructuredData 
        serviceName="Réparation de serrure"
        serviceDescription="Service de réparation de serrure à Villeurbanne : cylindre grippé, serrure endommagée, extraction de clé cassée. Intervention rapide 24h/7j."
        serviceUrl="https://www.tolstoi-serrurier.fr/reparation-serrure"
      />
      <Header />
      
      <div className="container mx-auto px-4">
        <Breadcrumbs items={[
          { label: "Accueil", href: "/" },
          { label: "Réparation de serrure", href: "/reparation-serrure" }
        ]} />
      </div>
      
      <main className="pt-24">
        {/* Hero Section with Background Image */}
        <section className="relative bg-secondary text-secondary-foreground py-16 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt="Serrurier Villeurbanne - Réparation de serrure endommagée"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-secondary/90 to-secondary/70" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Réparation de Serrure à Villeurbanne</h1>
              <p className="text-xl mb-8 text-secondary-foreground/90">Serrure endommagée, cylindre grippé ou clé cassée ? Réparation rapide et professionnelle</p>
              <Button size="lg" variant="default" className="gap-2" asChild><a href="tel:0482531608"><Phone className="h-5 w-5" />Appeler maintenant : 04 82 53 16 08</a></Button>
            </div>
          </div>
        </section>

        <SEOSubHero title="Réparation de serrure à Villeurbanne — diagnostic sur place">
          <p>
            Votre serrure accroche, le <strong>barillet</strong> est grippé ou la <strong>clé tourne dans le vide</strong> ? Nous réalisons un <strong>diagnostic sur place</strong> et privilégions la réparation (nettoyage, réglage, remplacement de pièces) pour éviter un changement complet.
          </p>
          <p>
            Devis annoncé avant intervention, facture assurance, technicien certifié. Besoin d'une intervention immédiate ? Consultez le <Link to="/depannage-urgence" className="text-primary hover:underline">dépannage urgence 24/7</Link>. Si la serrure est trop endommagée, nous proposons le{" "}
            <Link to="/changement-serrure" className="text-primary hover:underline">changement de serrure</Link>.
          </p>
          <p>
            Interventions rapides à Villeurbanne et Lyon (tous arrondissements). <Link to="/zones-intervention" className="text-primary hover:underline">Zones couvertes</Link>.
          </p>
        </SEOSubHero>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg max-w-none mb-12">
                <h2 className="text-3xl font-bold mb-6">Service de réparation de serrure professionnel</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Votre serrure ne fonctionne plus correctement ? Le cylindre est grippé ou la clé tourne dans le vide ? 
                  Tolstoï Serrurier intervient rapidement à Villeurbanne et dans toute la métropole lyonnaise pour 
                  diagnostiquer et réparer votre serrure défectueuse.
                </p>
                
                <h3 className="text-2xl font-semibold mb-4 mt-8">Diagnostic et réparation</h3>
                <p className="text-muted-foreground mb-6">
                  Notre serrurier professionnel réalise un diagnostic complet de votre serrure pour identifier 
                  l'origine du problème et vous proposer la solution la plus adaptée. Dans la plupart des cas, 
                  une réparation suffit et permet d'éviter le remplacement complet de la serrure.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card>
                  <CardContent className="pt-6">
                    <Wrench className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Diagnostic gratuit</h3>
                    <p className="text-sm text-muted-foreground">
                      Analyse complète du problème sur place avec explication détaillée et devis transparent.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <CheckCircle2 className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Réparation rapide</h3>
                    <p className="text-sm text-muted-foreground">
                      Intervention immédiate avec les outils et pièces nécessaires pour réparer votre serrure.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <AlertCircle className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Disponible 24h/7j</h3>
                    <p className="text-sm text-muted-foreground">
                      Service d'urgence disponible jour et nuit, week-ends et jours fériés inclus.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-muted/50 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Problèmes de serrure les plus fréquents</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Cylindre grippé ou qui ne tourne plus correctement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Clé qui tourne dans le vide sans actionner la serrure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Clé cassée coincée dans le cylindre</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Pêne qui ne rentre pas ou ne sort pas complètement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Serrure endommagée après une tentative d'effraction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Serrure multipoints dont un ou plusieurs points ne fonctionnent plus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span>Mécanisme de serrure usé ou rouillé</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary/10 rounded-xl p-8 mb-12">
                <h3 className="text-2xl font-semibold mb-4">Nos interventions de réparation</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Déblocage de cylindre</h4>
                    <p className="text-sm text-muted-foreground">
                      Nettoyage, lubrification et remise en état du mécanisme
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Extraction de clé cassée</h4>
                    <p className="text-sm text-muted-foreground">
                      Retrait de clé coincée sans endommager le cylindre
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Remplacement de pièces</h4>
                    <p className="text-sm text-muted-foreground">
                      Changement de cylindre, pêne, ressorts ou autres composants
                    </p>
                  </div>
                  <div className="bg-background p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Ajustement de porte</h4>
                    <p className="text-sm text-muted-foreground">
                      Réglage de l'alignement pour un bon fonctionnement
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-secondary text-secondary-foreground rounded-xl p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Besoin de réparer votre serrure ?</h3>
                <p className="text-lg mb-6 text-secondary-foreground/90">
                  Contactez-nous pour un diagnostic gratuit et une réparation rapide
                </p>
                <Button size="lg" variant="default" className="gap-2" asChild>
                  <a href="tel:0482531608">
                    <Phone className="h-5 w-5" />
                    04 82 53 16 08
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">Nos Interventions en Images</h2>
              <p className="text-center text-muted-foreground mb-12">Découvrez nos réalisations</p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="relative group overflow-hidden rounded-xl shadow-lg"><img src={gallery1} alt="Cylindre de serrure cassé endommagé nécessitant réparation Villeurbanne" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" /></div>
                <div className="relative group overflow-hidden rounded-xl shadow-lg"><img src={gallery2} alt="Serrurier démontant mécanisme serrure pour réparation Lyon" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" /></div>
                <div className="relative group overflow-hidden rounded-xl shadow-lg"><img src={gallery3} alt="Serrure réparée fonctionnelle clé tournant cylindre Villeurbanne" loading="lazy" className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500" /></div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <CallButton />
    </div>
  );
};

export default ReparationSerrure;
